import React from "react"
import clsx from "clsx"
import { Button, Chip, IconButton, TextField, Theme, Typography, makeStyles } from "@material-ui/core"
import Skeleton from "@material-ui/lab/Skeleton"
import { MessageCircle, Plus } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDebounce } from "../../hooks/use-debounce"
import ChatListItem from "./ChatListItem"
import { ChatResponse } from "../api/chat-response"
import { CHAT_FILTERS, ChatFilter, matchesChatFilter } from "../utils/chatUtil"

const SEARCH_DEBOUNCE_MS = 500

type Props = {
  chats: ChatResponse[]
  loading?: boolean
  selectedChatId: string | null
  canCreateChat: boolean
  hidden?: boolean
  onSelectChat?: (id: string) => void
  onCreate?: () => void
  onSearch: (search: string) => void
}

const ChatSidebar = ({
  chats,
  loading,
  selectedChatId,
  canCreateChat,
  hidden,
  onSelectChat,
  onCreate,
  onSearch,
}: Props) => {
  const classes = useStyles()
  const { t } = useTranslation("chat")
  const showCreateTrigger = canCreateChat && Boolean(onCreate)
  const [search, setSearch] = React.useState("")
  const [filter, setFilter] = React.useState<ChatFilter>("all")
  const { debouncedFn: commitSearch } = useDebounce(onSearch, SEARCH_DEBOUNCE_MS)
  const searching = search.trim().length > 0

  // A filter nobody can match is noise, but the selected one stays so its empty list is escapable.
  const filters = CHAT_FILTERS.filter(
    ({ value }) => value === "all" || value === filter || chats.some((chat) => matchesChatFilter(chat, value))
  )
  const visibleChats = chats.filter((chat) => matchesChatFilter(chat, filter))

  return (
    <div className={clsx(classes.root, hidden && classes.hidden)}>
      <div className={classes.header}>
        <Typography variant="h6">{t("chatSidebar.title")}</Typography>

        {showCreateTrigger && (
          <IconButton size="small" aria-label={t("chatSidebar.createChatButtonLabel")} onClick={onCreate}>
            <Plus size={18} />
          </IconButton>
        )}
      </div>

      <div className={classes.search}>
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder={t("chatSidebar.searchPlaceholder")}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            commitSearch(event.target.value)
          }}
        />
      </div>

      <div className={classes.filters}>
        {filters.map(({ value, labelKey }) => (
          <Chip
            key={value}
            size="small"
            label={t(labelKey)}
            clickable
            color={value === filter ? "primary" : "default"}
            onClick={() => setFilter(value)}
          />
        ))}
      </div>

      <div className={classes.list}>
        {loading && chats.length === 0 ? (
          Array.from({ length: 6 }, (_, index) => (
            <div key={index} className={classes.skeleton}>
              <Skeleton variant="circle" width={40} height={40} />

              <div className={classes.skeletonText}>
                <Skeleton width="60%" />
                <Skeleton width="35%" />
              </div>
            </div>
          ))
        ) : visibleChats.length > 0 ? (
          visibleChats.map((chat) => (
            <div key={chat.id} className={classes.chat} onClick={() => onSelectChat?.(chat.id)}>
              <ChatListItem
                title={chat.name}
                subtitle={chat.type}
                selected={chat.id === selectedChatId}
                unreadCount={chat.unreadCount}
              />
            </div>
          ))
        ) : (
          <div className={classes.emptyState}>
            <div className={classes.emptyIcon}>
              <MessageCircle size={28} />
            </div>

            <Typography className={classes.emptyTitle}>
              {searching || filter !== "all" ? t("chatSidebar.noResultsTitle") : t("chatSidebar.emptyTitle")}
            </Typography>

            <Typography className={classes.emptyDescription}>
              {searching
                ? t("chatSidebar.noSearchResultsDescription")
                : filter !== "all"
                ? t("chatSidebar.noFilterResultsDescription")
                : showCreateTrigger
                ? t("chatSidebar.emptyDescription")
                : t("chatSidebar.noAccessDescription")}
            </Typography>

            {showCreateTrigger && !searching && filter === "all" && (
              <Button
                color="primary"
                variant="contained"
                startIcon={<Plus size={18} />}
                onClick={onCreate}
                className={classes.createButton}
              >
                {t("chatSidebar.createChatButtonLabel")}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: 320,
    display: "flex",
    flexDirection: "column",
    borderRight: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,

    // On small screens the sidebar is the whole view until a chat is opened.
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      borderRight: "none",
    },
  },

  // Hidden rather than unmounted so the search text, filter and scroll position
  // survive opening a chat and coming back.
  hidden: {
    display: "none",
  },

  header: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  search: {
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  filters: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  list: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },

  chat: {
    cursor: "pointer",
  },

  skeleton: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
  },

  skeletonText: {
    flex: 1,
  },

  emptyState: {
    flex: 1,
    minHeight: 300,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: theme.palette.text.secondary,
  },

  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
    background: theme.palette.action.hover,
    color: theme.palette.text.secondary,
  },

  emptyTitle: {
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.75),
  },

  emptyDescription: {
    maxWidth: 240,
    lineHeight: 1.5,
    marginBottom: theme.spacing(2),
  },

  createButton: {
    marginTop: theme.spacing(1),
  },
}))

export default ChatSidebar
